import React, { useCallback, useState } from 'react';
import {
  ButtonNext,
  ContentBodyBox,
  ContentHeadingBox,
  CustomizedTable,
  MainLayout,
  ButtonUnderlined as ButtonBack
} from 'src/components';

import plusIcon from 'src/assets/images/icons/new_allocation_plus.svg';
import styles from 'src/components/workspaces-components/styles.module.scss';

import { ACCESS_TABLE_HEADINGS_LIST, renderTableContent } from './helpers';
import { PAGES } from 'src/constants/pages';
import { useHistory, useParams } from 'react-router-dom';
import { WORKSPACE_ACTIONS, WORKSPACE_FIELDS } from 'src/constants/workspacesConstants';
import ActionModal from 'src/components/workspaces-components/ActionModal';
import { getCompanyWorkspaceAccessesHook } from 'src/hooks/getCompanyWorkspaceAccessesHook';
import { useDispatch, useSelector } from 'react-redux';
import { workspaceAccessNameSelector } from 'src/redux/selectors';
import { TAccessSubmitValues } from 'src/interfaces/WorkspacesTypes';
import { addWorkspaceAccess, deleteWorkspaceAccess, editWorkspaceAccess } from 'src/redux/actions/workspacesActions';

const AccessWorkspacePage = (): JSX.Element => {
  const { push } = useHistory();
  const { id } = useParams<{ id: string }>();
  const { accesses, isLoading } = getCompanyWorkspaceAccessesHook();

  const workspaceName = useSelector(workspaceAccessNameSelector);

  const dispatch = useDispatch();

  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<null | WORKSPACE_ACTIONS>(null);

  const [actionError, setActionError] = useState<null | string>(null);

  const onToggleActionModal = () => setIsActionModalOpen((prev) => !prev);

  const onCloseActionModal = useCallback(() => {
    setIsActionModalOpen(false);
    setSelectedAction(null);
    setActionError(null);
    setUserName('');
    setUserEmail('');
    setUserId('');
  }, []);

  const onConfirmAction = useCallback(
    (values?: TAccessSubmitValues) => {
      const payloadHandlers = {
        onSuccess: () => {
          onCloseActionModal();
        },
        onError: (error) => {
          setActionError(error?.message || 'Something went wrong. Please try again.');
        }
      };

      if (selectedAction === WORKSPACE_ACTIONS.DELETE) {
        dispatch(deleteWorkspaceAccess({ workspaceId: id, userId, ...payloadHandlers }));
        return;
      }

      if (selectedAction === WORKSPACE_ACTIONS.GRANT_ACCESS) {
        const payload = {
          workspaceId: id,
          body: { email: values?.email, accessMode: values?.access },
          ...payloadHandlers
        };

        dispatch(addWorkspaceAccess(payload));
        return;
      }

      if (selectedAction === WORKSPACE_ACTIONS.EDIT_ACCESS) {
        const payload = {
          workspaceId: id,
          userId,
          body: { accessMode: values?.access },
          ...payloadHandlers
        };

        dispatch(editWorkspaceAccess(payload));
        return;
      }
    },
    [dispatch, id, onCloseActionModal, selectedAction, userId]
  );

  const onActionClick = useCallback(
    (action: WORKSPACE_ACTIONS, userName?: string, userEmail?: string, userId?: string) => {
      setSelectedAction(action);
      setIsActionModalOpen(true);
      setUserName(userName);
      setUserEmail(userEmail);
      setUserId(userId);
    },
    []
  );

  const tableContent = useCallback(
    (colId, row) => {
      return renderTableContent(colId, row, onActionClick);
    },
    [onActionClick]
  );

  const onBackButtonClick = () => {
    push(PAGES.WORKSPACES_MANAGEMENT);
  };

  return (
    <MainLayout isEmptySkeleton>
      <ContentHeadingBox>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>{workspaceName}</h2>

          <ButtonBack
            label="Back To Workspaces List"
            onClick={onBackButtonClick}
            classes={{ btnClass: styles.btnBack }}
          />
        </div>
      </ContentHeadingBox>

      <ContentBodyBox>
        <div className={styles.accessHeader}>
          <h3 className={styles.subtitle}>Access rights</h3>

          <ButtonNext
            label="Grant access"
            onClick={() => onActionClick(WORKSPACE_ACTIONS.GRANT_ACCESS)}
            beforeImg={plusIcon}
            type="button"
          />
        </div>

        <CustomizedTable
          columns={ACCESS_TABLE_HEADINGS_LIST}
          rows={accesses}
          onSort={() => undefined}
          isLoading={isLoading}
          guttersRight={45}
          guttersLeft={40}
          getSummaryContent={tableContent}
          hasDetails={false}
          tContainerClass={styles.tContainerClass}
          emptyRowsMsg={'No users found'}
          maxTableContainerHeight="unset"
        />
      </ContentBodyBox>

      <ActionModal
        action={selectedAction}
        isOpen={isActionModalOpen}
        onToggleModal={onToggleActionModal}
        onClose={onCloseActionModal}
        onConfirm={onConfirmAction}
        errorMsg={actionError}
        title="Access rights"
        deleteText={`Are you sure you want to revoke access rights of ${userName} from ${workspaceName} workspace? `}
        inputValue={{ [WORKSPACE_FIELDS.EMAIL]: userEmail }}
        onErrorClick={() => setActionError(null)}
      />
    </MainLayout>
  );
};

export default AccessWorkspacePage;
